import React, { useRef, useEffect } from 'react';
import {
  Typography,
  Box,
  Grid,
  Button,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Stack,
  Fab
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  ContentCut as ContentCutIcon,
  Spa as SpaIcon,
  Face as FaceIcon,
  Brush as BrushIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon
} from '@mui/icons-material';

const categories = [
  { name: 'Salon de coiffure ou barbershop', icon: ContentCutIcon },
  { name: 'Institut de beauté ou bar à ongles', icon: FaceIcon },
  { name: 'Spa ou centre de bien-être', icon: SpaIcon },
  { name: 'Salon d\'esthétique', icon: BrushIcon },
  { name: 'Centre de bronzage', icon: SpaIcon },
  { name: 'Salon de massage', icon: SpaIcon },
  { name: 'Extensions de cils', icon: FaceIcon },
  { name: 'Tatouage et piercing', icon: BrushIcon }
];

const CategorySelectionStep = ({ formData, setFormData, onBack, onNext }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const continueButtonRef = useRef(null);

  // Auto-scroll to continue button when category is selected
  useEffect(() => {
    if (formData.category && isMobile && continueButtonRef.current) {
      setTimeout(() => {
        continueButtonRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }, 300);
    }
  }, [formData.category, isMobile]);

  if (isMobile) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600, 
            mb: 3, 
            color: 'white',
            fontSize: '1.1rem',
            textAlign: 'center'
          }}
        >
          Quel établissement souhaitez-vous équiper ?
        </Typography>
        
        <Stack spacing={2} sx={{ flex: 1, mb: 3 }}>
          {categories.map((category) => {
            const IconComponent = category.icon;
            return (
              <Card 
                key={category.name}
                sx={{ 
                  cursor: 'pointer',
                  border: formData.category === category.name ? '2px solid #f5f5dc' : '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  background: formData.category === category.name
                    ? 'linear-gradient(135deg, rgba(245,245,220,0.15) 0%, rgba(232,232,200,0.15) 100%)'
                    : 'rgba(255,255,255,0.05)',
                  '&:active': {
                    transform: 'scale(0.98)'
                  }
                }}
                onClick={() => setFormData(prev => ({ ...prev, category: category.name }))}
              >
                <CardContent sx={{ py: 2, px: 2.5 }}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: formData.category === category.name 
                          ? 'linear-gradient(135deg, #f5f5dc 0%, #e8e8c8 100%)'
                          : 'rgba(255,255,255,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <IconComponent 
                        sx={{ 
                          fontSize: 20, 
                          color: formData.category === category.name ? '#212121' : '#f5f5dc'
                        }} 
                      />
                    </Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'white', 
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        lineHeight: 1.3,
                        flex: 1
                      }}
                    >
                      {category.name}
                    </Typography>
                    <ArrowForwardIcon 
                      sx={{ 
                        color: formData.category === category.name ? '#f5f5dc' : 'rgba(255,255,255,0.4)',
                        fontSize: '1.2rem'
                      }} 
                    />
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Stack>

        <Box display="flex" gap={2} ref={continueButtonRef}>
          <Button
            variant="outlined"
            onClick={onBack}
            startIcon={<ArrowBackIcon />}
            sx={{
              flex: 1,
              py: 1.5,
              borderColor: 'rgba(255,255,255,0.3)',
              color: 'rgba(255,255,255,0.8)',
              '&:hover': {
                borderColor: '#f5f5dc',
                backgroundColor: 'rgba(245,245,220,0.1)',
                color: '#f5f5dc'
              }
            }}
          >
            Retour
          </Button>
          <Button
            variant="contained"
            disabled={!formData.category}
            onClick={onNext}
            endIcon={<ArrowForwardIcon />}
            sx={{ 
              flex: 2,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              fontSize: '0.9rem',
              background: 'linear-gradient(135deg, #f5f5dc 0%, #e8e8c8 100%)',
              color: '#212121',
              boxShadow: '0 4px 15px rgba(245, 245, 220, 0.3)',
              '&:disabled': {
                background: 'rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.3)'
              }
            }}
          >
            Continuer
          </Button>
        </Box>

        {/* Mobile scroll indicator */}
        {!formData.category && (
          <Fab
            size="small"
            sx={{
              position: 'fixed',
              bottom: 20,
              right: 20,
              zIndex: 1000,
              background: 'linear-gradient(135deg, #f5f5dc 0%, #e8e8c8 100%)',
              color: '#212121',
              boxShadow: '0 4px 20px rgba(245, 245, 220, 0.3)',
              animation: 'bounce 2s infinite',
              '@keyframes bounce': {
                '0%, 20%, 50%, 80%, 100%': {
                  transform: 'translateY(0)'
                },
                '40%': {
                  transform: 'translateY(-10px)'
                },
                '60%': {
                  transform: 'translateY(-5px)'
                }
              },
              '&:hover': {
                background: 'linear-gradient(135deg, #e8e8c8 0%, #dcdcb8 100%)'
              }
            }}
            onClick={() => {
              window.scrollTo({
                top: document.documentElement.scrollHeight,
                behavior: 'smooth'
              });
            }}
          >
            <KeyboardArrowDownIcon />
          </Fab>
        )}
      </Box>
    );
  }

  // Desktop version
  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 4, color: 'white' }}>
        Quel établissement souhaitez-vous équiper ?
      </Typography>
      
      <Grid container spacing={2}>
        {categories.map((category) => {
          const IconComponent = category.icon;
          return (
            <Grid item xs={12} key={category.name}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  border: formData.category === category.name ? '3px solid #f5f5dc' : '2px solid rgba(255,255,255,0.1)',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  background: formData.category === category.name
                    ? 'linear-gradient(135deg, rgba(245,245,220,0.1) 0%, rgba(232,232,200,0.1) 100%)'
                    : 'rgba(255,255,255,0.05)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(245, 245, 220, 0.2)',
                    border: '3px solid #f5f5dc',
                    background: 'rgba(245,245,220,0.1)'
                  }
                }}
                onClick={() => setFormData(prev => ({ ...prev, category: category.name }))}
              >
                <CardContent sx={{ py: 2 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={2}>
                      <IconComponent sx={{ color: '#f5f5dc', fontSize: 24 }} />
                      <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                        {category.name}
                      </Typography>
                    </Box>
                    <ArrowForwardIcon sx={{ color: formData.category === category.name ? '#f5f5dc' : 'rgba(255,255,255,0.6)' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Box display="flex" justifyContent="space-between" mt={4}>
        <Button
          variant="text"
          onClick={onBack}
          startIcon={<ArrowBackIcon />}
          sx={{
            color: 'rgba(255,255,255,0.7)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)',
              color: '#f5f5dc'
            }
          }}
        >
          Retour
        </Button>
        <Button
          variant="contained"
          disabled={!formData.category}
          onClick={onNext}
          endIcon={<ArrowForwardIcon />}
          sx={{ 
            px: 4,
            py: 1.5,
            borderRadius: 3,
            fontWeight: 600,
            background: 'linear-gradient(135deg, #f5f5dc 0%, #e8e8c8 100%)',
            color: '#212121',
            boxShadow: '0 8px 25px rgba(245, 245, 220, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #e8e8c8 0%, #dcdcb8 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 35px rgba(245, 245, 220, 0.4)'
            },
            '&:disabled': {
              background: 'rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.3)'
            }
          }}
        >
          Continuer
        </Button>
      </Box>
    </Box>
  );
};

export default CategorySelectionStep;